import React from 'react';
import { useAppSelector } from '../../store/hooks.ts';
import { selectCurrentOrganization } from '../../store/organization/organizationSlice.ts';
import { Alert, Box, Typography, Tabs, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TmRiskTable from './components/TmRiskTable.tsx';
import TmMitigationTable from './components/TmMitigationTable.tsx';
import TmActorTable from './components/TmActorTable.tsx';
import TmAssessmentSelector from './components/TmAssessmentSelector.tsx';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`threat-modeling-tabpanel-${index}`}
      aria-labelledby={`threat-modeling-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ThreatModelingModule: React.FC = () => {
  const currentOrg = useAppSelector(selectCurrentOrganization);
  const [tabValue, setTabValue] = React.useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const tabPaths = React.useMemo(() => ['/risks', '/mitigations', '/actors'], []);

  React.useEffect(() => {
    const matchedIndex = tabPaths.findIndex(path => location.pathname.endsWith(path));
    if (matchedIndex !== -1 && matchedIndex !== tabValue) {
      setTabValue(matchedIndex);
    }
  }, [location.pathname, tabPaths, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newPath = tabPaths[newValue];
    if (newPath && location.pathname !== newPath) {
      navigate(newPath);
    }
  };

  if (!currentOrg?.threat_modeling_enabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Threat Modeling module is not enabled for this organization.
          Contact your organization administrator to enable it in the organization settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Threat Modeling
      </Typography>

      <TmAssessmentSelector />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="threat modeling tabs">
          <Tab label="Risks" id="threat-modeling-tab-0" />
          <Tab label="Mitigations" id="threat-modeling-tab-1" />
          <Tab label="Actors" id="threat-modeling-tab-2" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <TmRiskTable />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TmMitigationTable />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TmActorTable />
      </TabPanel>
    </Box>
  );
};

export default ThreatModelingModule;
